import { useEffect, useState } from 'react';
import { getChatStore } from '@/features/chat-feed/hooks';
import type { Platform, ChatEvent } from '@multimod/shared';

/**
 * Calcula msg/min rolling 60s sobre o feed de uma live, atualizando a cada 5s.
 *
 * Usa `store.getState()` (não-reactive) combinado com setInterval para evitar
 * regras de hooks condicionais e minimizar re-renders desnecessários.
 * O interval de 5s força recompute mesmo quando não chegam novas mensagens
 * (msgs envelhecem e saem da janela de 60s).
 *
 * Não chama useChatFeed — lê o store já existente via getChatStore sem
 * abrir WS extra (ver hooks.ts).
 */
export function useMsgRate(liveChatId: string | null, platform: Platform | null): number {
  const [, setTick] = useState(0);

  // tick interval para re-renderizar mesmo sem nova mensagem (msgs envelhecem)
  useEffect(() => {
    if (!liveChatId) return;
    const id = setInterval(() => setTick((t) => t + 1), 5_000);
    return () => clearInterval(id);
  }, [liveChatId]);

  if (!liveChatId || !platform) return 0;
  const store = getChatStore(liveChatId);
  if (!store) return 0;

  // Usa getState() (snapshot não-reactive) — o interval de 5s força recompute.
  // Evita chamar store(selector) condicionalmente, que violaria rules of hooks.
  const messages = store.getState().messages;
  const cutoff = Date.now() - 60_000;
  return messages.filter(
    (m): m is Extract<ChatEvent, { type: 'message' }> =>
      m.type === 'message' && m.platform === platform && m.timestamp >= cutoff,
  ).length;
}
